import Box from "@mui/material/Box";
import type {ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {type Sector, SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {
    endOfDay,
    endOfMonth,
    endOfWeek,
    startOfDay,
    startOfMonth,
    startOfWeek,
} from "crqlar-react-common/dist/utils/date.util";
import {useCurrentAppId} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {safeParseNumber} from "crqlar-react-common/dist/utils/parse.utils";
import {useEffect, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {ReservationActions} from "../../store/reservations.reducer";
import {useCompactDashboardLayout} from "../../utils/responsive.util";
import {defaultStatusFiltersBasedOnView, getPermittedSteps} from "../../utils/reservation.util";
import TableReservationTimelineTab from "./calendar/ReservationTimelineTab";

type CalendarSpan = "day" | "week" | "month";
type CalendarViewMode = "timeline" | "timeGrid";
type CalendarResourceColumnType = "resources" | "services";

const isCalendarSpan = (value: string | null): value is CalendarSpan =>
    value === "day" || value === "week" || value === "month";

const isCalendarViewMode = (value: string | null): value is CalendarViewMode =>
    value === "timeline" || value === "timeGrid";

const getStoredStatuses = (sectorId: string): ReservationStatus[] => {
    const key = `selectedStatuses-${sectorId}-calendar`;
    const storedStatuses = localStorage.getItem(key);
    const statuses = storedStatuses === null
        ? defaultStatusFiltersBasedOnView("calendar") ?? []
        : storedStatuses
            ? storedStatuses.split(",") as ReservationStatus[]
            : [];

    localStorage.setItem(key, statuses.join(","));
    return statuses;
};

const getCalendarDefaults = (
    sectorType: SectorType | undefined,
    defaultTimelineSpan?: string,
    defaultTimelineStep?: number,
): {
    span: CalendarSpan;
    step: number;
    viewMode: CalendarViewMode;
} => {
    switch (sectorType) {
        case SectorType.RESTAURANT:
            return {span: "day", step: 30, viewMode: "timeline"};
        case SectorType.SPA:
            return {
                span: isCalendarSpan(defaultTimelineSpan ?? null)
                    ? defaultTimelineSpan as CalendarSpan
                    : "day",
                step: defaultTimelineStep || 30,
                viewMode: "timeGrid",
            };
        case SectorType.ACCOMMODATION:
            return {
                span: isCalendarSpan(defaultTimelineSpan ?? null)
                    ? defaultTimelineSpan as CalendarSpan
                    : "month",
                step: defaultTimelineStep || 1440,
                viewMode: "timeline",
            };
        default:
            return {
                span: isCalendarSpan(defaultTimelineSpan ?? null)
                    ? defaultTimelineSpan as CalendarSpan
                    : "day",
                step: defaultTimelineStep || 30,
                viewMode: "timeline",
            };
    }
};

const getDateRange = (span: CalendarSpan): [Date, Date] => {
    const now = new Date();

    switch (span) {
        case "week":
            return [startOfWeek(now), endOfWeek(now)];
        case "month":
            return [startOfMonth(now), endOfMonth(now)];
        default:
            return [startOfDay(now), endOfDay(now)];
    }
};

export function ReservationCalendar({sector}: {sector: Sector}) {
    const dispatch = useDispatch();
    const appId = useCurrentAppId();
    const isCompactLayout = useCompactDashboardLayout();
    const calendarKey = appId && sector.id ? `${appId}:${sector.id}:calendar` : undefined;
    const [initializedCalendarKey, setInitializedCalendarKey] = useState<string>();
    const defaults = useMemo(
        () => getCalendarDefaults(
            sector.sectorType,
            sector.defaultTimelineSpan,
            sector.defaultTimelineStep,
        ),
        [sector.defaultTimelineSpan, sector.defaultTimelineStep, sector.sectorType],
    );

    useTrackScreenVisit("reservation schedule calendar");

    useEffect(() => {
        if (!appId || !sector.id || !calendarKey) return;

        const storedViewMode = localStorage.getItem(`viewMode-${sector.id}`);
        const viewMode = isCalendarViewMode(storedViewMode)
            ? storedViewMode
            : defaults.viewMode;
        const storedSpan = localStorage.getItem(`timelineSpan-${sector.id}`);
        const requestedSpan = isCalendarSpan(storedSpan) ? storedSpan : defaults.span;
        const timelineSpan = viewMode === "timeGrid" ? "day" : requestedSpan;
        const permittedSteps = getPermittedSteps(timelineSpan);
        const storedStep = safeParseNumber(localStorage.getItem(`timelineStep-${sector.id}`));
        const defaultStep = timelineSpan === defaults.span
            ? defaults.step
            : timelineSpan === "month"
                ? 60
                : 30;
        const timelineStep = storedStep && permittedSteps.includes(storedStep)
            ? storedStep
            : permittedSteps.includes(defaultStep)
                ? defaultStep
                : permittedSteps[0];
        const storedColumnType = localStorage.getItem(`mbscResourceColumnType-${sector.id}`);
        const isAccommodation = sector.sectorType === SectorType.ACCOMMODATION;
        const resourceColumnType: CalendarResourceColumnType = !isAccommodation && storedColumnType === "services"
            ? "services"
            : "resources";
        const resourceCategoryFilter = isAccommodation
            ? undefined
            : localStorage.getItem(`resourceCategoryFilter-${sector.id}`) || undefined;
        const [fromDate, toDate] = getDateRange(timelineSpan);

        localStorage.setItem(`viewMode-${sector.id}`, viewMode);
        localStorage.setItem(`timelineSpan-${sector.id}`, timelineSpan);
        localStorage.setItem(`timelineStep-${sector.id}`, String(timelineStep));
        localStorage.setItem(`mbscResourceColumnType-${sector.id}`, resourceColumnType);
        if (isAccommodation) {
            localStorage.removeItem(`resourceCategoryFilter-${sector.id}`);
        }

        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId,
            payload: {
                selectedResourceSingle: undefined,
                selectedStatuses: getStoredStatuses(sector.id),
                searchQuery: undefined,
                reservationDaySlice: localStorage.getItem(`reservationDaySlice-${sector.id}`) || undefined,
                activeTab: "calendar",
                resourceCategoryFilter,
                timelineSpan,
                timelineStep,
                viewMode,
                mbscResourceColumnType: resourceColumnType,
                startTimeFrame: localStorage.getItem(`startTimeFrame-${sector.id}`) || "00:00",
                endTimeFrame: localStorage.getItem(`endTimeFrame-${sector.id}`) || "23:59",
                fromDate: fromDate.toISOString(),
                toDate: toDate.toISOString(),
                initialized: true,
            },
        });
        setInitializedCalendarKey(calendarKey);
    }, [appId, calendarKey, defaults, dispatch, sector.id, sector.sectorType]);

    if (!calendarKey || initializedCalendarKey !== calendarKey) return null;

    return (
        <Box
            sx={{
                display: "flex",
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                width: "100%",
                height: isCompactLayout
                    ? "calc(100dvh - 56px)"
                    : "calc(100dvh - 108px)",
                "@supports not (height: 100dvh)": {
                    height: isCompactLayout
                        ? "calc(100vh - 56px)"
                        : "calc(100vh - 108px)",
                },
                position: "relative",
                overflow: "hidden",
            }}
        >
            <TableReservationTimelineTab/>
        </Box>
    );
}
